import { useState, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Upload, 
  CheckCircle, 
  XCircle, 
  Image as ImageIcon,
  Loader2,
  ArrowLeft,
  Camera,
  FileImage,
  DollarSign,
  Banknote
} from "lucide-react";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";

const RentReceiptUpload = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const paymentId = searchParams.get("payment_id");
  const paymentType = searchParams.get("type") || "digital"; // digital or cash
  
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [notes, setNotes] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploaded, setUploaded] = useState(false);
  const [cashConfirmed, setCashConfirmed] = useState(false);
  const [confirmingCash, setConfirmingCash] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(paymentType === "cash" ? "cash" : "receipt");
  const fileInputRef = useRef(null);

  const handleCashConfirmation = async () => {
    if (!paymentId) {
      setError("Invalid payment link");
      return;
    }

    setConfirmingCash(true);
    setError(null);

    try {
      await axios.post(`${API}/booth/rent-payment/${paymentId}/confirm-cash-payment`);
      setCashConfirmed(true);
      toast.success("Cash payment confirmed! The owner has been notified.");
    } catch (err) {
      console.error("Cash confirmation error:", err);
      setError(err.response?.data?.detail || "Failed to confirm cash payment");
      toast.error("Confirmation failed");
    } finally {
      setConfirmingCash(false);
    }
  };

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    if (!selectedFile.type.startsWith('image/')) {
      toast.error("Please select an image file (PNG, JPG, etc.)");
      return;
    }

    if (selectedFile.size > 10 * 1024 * 1024) { // 10MB limit
      toast.error("File is too large. Please select an image under 10MB.");
      return;
    }

    setFile(selectedFile);
    setError(null);

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreview(reader.result);
    };
    reader.readAsDataURL(selectedFile);
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error("Please select a receipt image first");
      return;
    }

    if (!paymentId) {
      setError("Invalid payment link. Please use the link from your email.");
      return;
    }

    setUploading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('notes', notes || 'Receipt uploaded by renter');

      await axios.post(
        `${API}/booth/rent-payment/${paymentId}/upload-receipt-public`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      setUploaded(true);
      toast.success("Receipt uploaded successfully!");
    } catch (err) {
      console.error("Upload error:", err);
      setError(err.response?.data?.detail || "Failed to upload receipt. Please try again.");
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  if (!paymentId) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-100 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h1 className="text-xl font-bold text-gray-900 mb-2">Invalid Link</h1>
            <p className="text-gray-600">
              This receipt upload link is invalid. Please use the link from your rent payment email.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Success state for receipt upload
  if (uploaded) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-12 h-12 text-green-500" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Receipt Uploaded!</h1>
            <p className="text-gray-600 mb-6">
              Your payment receipt has been submitted for verification. The salon owner has been notified and will confirm your payment shortly.
            </p>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <p className="text-blue-800 text-sm">
                <strong>What happens next?</strong><br />
                The salon owner will verify your receipt and mark your payment as complete. You'll receive a confirmation once verified.
              </p>
            </div>
            <Button 
              variant="outline"
              onClick={() => window.close()}
              className="w-full"
            >
              Close Window
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Success state for cash confirmation
  if (cashConfirmed) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-green-100 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Banknote className="w-12 h-12 text-emerald-500" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Cash Payment Reported!</h1>
            <p className="text-gray-600 mb-6">
              The salon owner has been notified that you've paid in cash. They will verify and confirm your payment.
            </p>
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 mb-6">
              <p className="text-emerald-800 text-sm">
                <strong>What happens next?</strong><br />
                The salon owner will check that they received your cash payment and mark it as complete in their system.
              </p>
            </div>
            <Button 
              variant="outline"
              onClick={() => window.close()}
              className="w-full"
            >
              Close Window
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-100 py-8 px-4">
      <div className="max-w-lg mx-auto">
        <Card>
          <CardHeader className="text-center border-b">
            <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <DollarSign className="w-8 h-8 text-amber-600" />
            </div>
            <CardTitle className="text-2xl">Confirm Your Payment</CardTitle>
            <p className="text-gray-500 mt-2">
              Choose how you paid your rent
            </p>
          </CardHeader>
          
          <CardContent className="p-6">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-6">
                <TabsTrigger value="receipt" className="data-[state=active]:bg-amber-500 data-[state=active]:text-white">
                  <Upload className="w-4 h-4 mr-2" />
                  Digital Payment
                </TabsTrigger>
                <TabsTrigger value="cash" className="data-[state=active]:bg-emerald-500 data-[state=active]:text-white">
                  <Banknote className="w-4 h-4 mr-2" />
                  Cash Payment
                </TabsTrigger>
              </TabsList>

              {/* Digital Payment - Receipt Upload */}
              <TabsContent value="receipt" className="space-y-6">
                {/* Instructions */}
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <h3 className="font-semibold text-amber-800 mb-2">📋 For CashApp, Zelle, or PayPal:</h3>
                  <ol className="text-amber-700 text-sm space-y-2 list-decimal list-inside">
                    <li>Complete your payment via the app</li>
                    <li>Take a <strong>screenshot</strong> of the confirmation</li>
                    <li>Upload the screenshot below</li>
                  </ol>
                </div>

                {/* File Upload Area */}
                <div>
                  <Label className="text-base font-medium mb-2 block">Payment Receipt Screenshot</Label>
                  
                  <div 
                    onClick={() => fileInputRef.current?.click()}
                    className={`
                      border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all
                      ${preview 
                        ? 'border-green-400 bg-green-50' 
                        : 'border-gray-300 hover:border-amber-400 hover:bg-amber-50'
                      }
                    `}
                  >
                    {preview ? (
                      <div className="space-y-4">
                        <img 
                          src={preview} 
                          alt="Receipt preview" 
                          className="max-h-48 mx-auto rounded-lg shadow-md"
                        />
                        <p className="text-green-600 font-medium flex items-center justify-center gap-2">
                          <CheckCircle className="w-5 h-5" />
                          {file?.name}
                        </p>
                        <Button variant="outline" size="sm" type="button">
                          Change Image
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto">
                          <Camera className="w-8 h-8 text-gray-400" />
                        </div>
                        <div>
                          <p className="text-gray-700 font-medium">Click to upload receipt</p>
                          <p className="text-gray-500 text-sm mt-1">PNG, JPG or JPEG (max 10MB)</p>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                </div>

                {/* Notes */}
                <div>
                  <Label className="text-base font-medium mb-2 block">Additional Notes (Optional)</Label>
                  <Textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="e.g., Paid via CashApp on Jan 10, 2026"
                    rows={2}
                  />
                </div>

                {/* Error Message */}
                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <p className="text-red-700 text-sm flex items-center gap-2">
                      <XCircle className="w-5 h-5" />
                      {error}
                    </p>
                  </div>
                )}

                {/* Upload Button */}
                <Button
                  onClick={handleUpload}
                  disabled={!file || uploading}
                  className="w-full h-12 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-bold text-lg"
                >
                  {uploading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="w-5 h-5 mr-2" />
                      Submit Receipt
                    </>
                  )}
                </Button>
              </TabsContent>

              {/* Cash Payment Confirmation */}
              <TabsContent value="cash" className="space-y-6">
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                  <h3 className="font-semibold text-emerald-800 mb-2">💵 Paid Cash at the Salon?</h3>
                  <p className="text-emerald-700 text-sm">
                    If you've already paid your rent in cash to the salon owner, click the button below to notify them.
                  </p>
                </div>

                <div className="text-center py-6">
                  <div className="w-24 h-24 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Banknote className="w-12 h-12 text-emerald-500" />
                  </div>
                  <p className="text-gray-600 mb-2">
                    This will send a notification to the salon owner to verify your cash payment.
                  </p>
                  <p className="text-sm text-gray-500">
                    Your payment will be marked as "Pending" until the owner confirms receipt.
                  </p>
                </div>

                {/* Error Message */}
                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <p className="text-red-700 text-sm flex items-center gap-2">
                      <XCircle className="w-5 h-5" />
                      {error}
                    </p>
                  </div>
                )}

                {/* Confirm Cash Button */}
                <Button
                  onClick={handleCashConfirmation}
                  disabled={confirmingCash}
                  className="w-full h-12 bg-gradient-to-r from-emerald-500 to-green-500 hover:from-emerald-600 hover:to-green-600 text-white font-bold text-lg"
                >
                  {confirmingCash ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Notifying Owner...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-5 h-5 mr-2" />
                      I've Paid Cash - Notify Owner
                    </>
                  )}
                </Button>
              </TabsContent>
            </Tabs>

            <p className="text-center text-gray-500 text-sm mt-6">
              The salon owner will verify and confirm your payment.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default RentReceiptUpload;
